import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { communicationsService, clientsService, tasksService, workflowStagesService } from '../../services';
import './CommunicationLogger.css';

const CommunicationLogger = ({ clientId, taskId, workflowStageId, communication, onClose, onSaved }) => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [workflowStages, setWorkflowStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isEditMode = !!communication;

  const [formData, setFormData] = useState({
    client_id: communication?.client_id || clientId || '',
    communication_type: communication?.communication_type || 'email',
    direction: communication?.direction || 'outbound',
    subject: communication?.subject || '',
    content: communication?.content || '',
    communication_date: communication?.communication_date 
      ? new Date(communication.communication_date).toISOString().slice(0, 16)
      : new Date().toISOString().slice(0, 16),
    duration_minutes: communication?.duration_minutes || '',
    follow_up_required: communication?.follow_up_required || false,
    follow_up_date: communication?.follow_up_date 
      ? new Date(communication.follow_up_date).toISOString().slice(0, 16)
      : '',
    tags: communication?.tags || '',
    workflow_stage_id: communication?.workflow_stage_id || workflowStageId || '',
    client_task_id: communication?.client_task_id || taskId || ''
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [clientsData, workflowStagesData] = await Promise.all([
        clientsService.getAll(),
        workflowStagesService.getStages()
      ]);
      
      setClients(clientsData);
      setWorkflowStages(workflowStagesData);

      // If client is pre-selected or editing, fetch their tasks
      const selectedClientId = communication?.client_id || clientId;
      if (selectedClientId) {
        const tasksData = await tasksService.getTasks({ client_id: selectedClientId });
        setTasks(tasksData);
      }
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
      setError('Failed to load form data. Please try again.');
    }
  };

  const handleClientChange = async (selectedClientId) => {
    setFormData({ ...formData, client_id: selectedClientId, client_task_id: '' });
    
    if (selectedClientId) {
      try {
        const tasksData = await tasksService.getTasks({ client_id: selectedClientId });
        setTasks(tasksData);
      } catch (error) {
        console.error('Failed to fetch client tasks:', error);
      }
    } else {
      setTasks([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const communicationData = {
        ...formData,
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
        follow_up_date: formData.follow_up_required && formData.follow_up_date ? formData.follow_up_date : null,
        tags: formData.tags.trim() || null
      };

      if (isEditMode) {
        await communicationsService.updateCommunication(communication.id, communicationData);
      } else {
        await communicationsService.createCommunication(communicationData);
      }
      
      if (onSaved) {
        onSaved();
      }
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'log'} communication:`, error);
      setError(`Failed to ${isEditMode ? 'update' : 'log'} communication. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email': return '📧';
      case 'phone': return '📞';
      case 'meeting': return '🤝';
      case 'text': return '💬';
      default: return '📝';
    }
  };

  const getDirectionColor = (direction) => {
    return direction === 'inbound' ? 'text-blue-600' : 'text-green-600';
  };

  return (
    <div className="communication-logger">
      <div className="logger-header">
        <h3 className="text-lg font-medium text-gray-900">
          {isEditMode ? 'Edit Communication' : 'Log Communication'}
        </h3>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Client Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client *
            </label>
            <select
              required
              value={formData.client_id}
              onChange={(e) => handleClientChange(e.target.value)}
              disabled={!!clientId} // Disable if client is pre-selected
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a client</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.first_name} {client.last_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Related Task (Optional)
            </label>
            <select
              value={formData.client_task_id}
              onChange={(e) => setFormData({ ...formData, client_task_id: e.target.value })}
              disabled={!!taskId} // Disable if task is pre-selected
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No related task</option>
              {tasks.map((task) => (
                <option key={task.id} value={task.id}>
                  {task.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Communication Type and Direction */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type *
            </label>
            <select
              required
              value={formData.communication_type}
              onChange={(e) => setFormData({ ...formData, communication_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="email">📧 Email</option>
              <option value="phone">📞 Phone Call</option>
              <option value="meeting">🤝 Meeting</option>
              <option value="text">💬 Text/SMS</option>
              <option value="quote_message">💼 Quote Message</option>
              <option value="other">📝 Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Direction *
            </label>
            <select
              required
              value={formData.direction}
              onChange={(e) => setFormData({ ...formData, direction: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="outbound">📤 Outbound (I contacted them)</option>
              <option value="inbound">📥 Inbound (They contacted me)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Duration (minutes)
            </label>
            <input
              type="number"
              min="1"
              value={formData.duration_minutes}
              onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Optional"
            />
          </div>
        </div>

        {/* Date and Time */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date & Time *
            </label>
            <input
              type="datetime-local"
              required
              value={formData.communication_date}
              onChange={(e) => setFormData({ ...formData, communication_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Workflow Stage (Optional)
            </label>
            <select
              value={formData.workflow_stage_id}
              onChange={(e) => setFormData({ ...formData, workflow_stage_id: e.target.value })}
              disabled={!!workflowStageId} // Disable if stage is pre-selected
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No specific stage</option>
              {workflowStages.map((stage) => (
                <option key={stage.id} value={stage.id}>
                  {stage.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Subject */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Subject/Topic
          </label>
          <input
            type="text"
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Brief description of the communication topic"
          />
        </div>

        {/* Content */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes/Content
          </label>
          <textarea
            rows={4}
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Detailed notes about the communication..."
          />
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tags
          </label>
          <input
            type="text"
            value={formData.tags}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Comma-separated tags (e.g., urgent, booking, payment)"
          />
          <p className="mt-1 text-xs text-gray-500">
            Use tags to categorize and search communications later
          </p>
        </div>

        {/* Follow-up */}
        <div className="space-y-3">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="follow_up_required"
              checked={formData.follow_up_required}
              onChange={(e) => setFormData({ ...formData, follow_up_required: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="follow_up_required" className="ml-2 block text-sm text-gray-900">
              Follow-up required
            </label>
          </div>

          {formData.follow_up_required && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Follow-up Date & Time
              </label>
              <input
                type="datetime-local"
                value={formData.follow_up_date}
                onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (isEditMode ? 'Updating...' : 'Logging...') : (isEditMode ? 'Update Communication' : 'Log Communication')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CommunicationLogger;